import React from 'react';
import ReactDOM from 'react-dom';
import '@sto/cn-ui/es/global'
import AIChart from './AIChart';
import { R } from './api/request';
import { getCookie } from './utils/tools';
import { ENV } from './configs/baseUrl';
import styles from './index.module.scss'
import Global from './utils/global';

// 全局 ResizeObserver 错误处理
const originalConsoleError = console.error;
console.error = (...args) => {
  // 过滤掉 ResizeObserver 的循环错误，避免在控制台显示
  if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
    return;
  }
  originalConsoleError.apply(console, args);
};

// 全局错误处理
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    event.preventDefault();
    return false;
  }
});

// 全局未捕获的 Promise 错误处理
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    event.preventDefault();
    return false;
  }
});

export { default as Question } from './AIChart/components/chat/components/question';
export { default as Answer } from './AIChart/components/chat/components/answer';

// const env = process.env.NODE_ENV;

// 机器人信息接口
interface RobotInfo {
  robotCode: string;
  [key: string]: any;
}

// 机器人列表项接口
interface RobotListItem {
  robotCode: string;
  [key: string]: any;
}

// 头部信息接口
interface HeaderInfo {
  isTestChat?: boolean;
  [key: string]: any;
}

// 组件集合接口
interface Components {
  [key: string]: React.ComponentType<any>;
}

// 机器人控制实例接口
interface RobotControlInstance {
  showRobot: () => void;
  hideRobot: () => void;
  toggleRobot: () => boolean;
}

// AIChart 组件实例接口
interface AIChartInstance {
  setBotVisible: (visible: boolean) => void;
  getBotVisible: () => boolean;
}

// 初始化参数接口
interface InitWidgetParams {
  token?: string;
  tokenKey?: string;
  robotCode?: string;
  robotList?: RobotListItem[];
  header?: HeaderInfo;
  autoloadingCss?: boolean;
  components?: Components;
  TriggerComponent?: React.ComponentType<any>;
  hideTriggerButton?: boolean;
  /** 自定义流式输出接口URL，支持完整URL或基础URL（会自动拼接/chat/ask/stream） */
  streamUrl?: string;
}

// 返回控制接口
interface WidgetControls {
  show: () => void;
  hide: () => void;
  toggle: () => boolean;
}

// 获取token响应接口
interface TokenResponse {
  token?: string;
}

// Global control for robot modal visibility
let robotControlInstance: RobotControlInstance | null = null;

const initWidget = async ({
  token,
  tokenKey,
  robotCode: code,
  robotList,
  header,
  autoloadingCss,
  components,
  TriggerComponent,
  hideTriggerButton,
  streamUrl
}: InitWidgetParams): Promise<WidgetControls> => {
  try {
    Global.components = {
      ...Global.components,
      ...components,
    };

    // 默认取浏览器缓存的机器人 code
    let storageRobot = localStorage.getItem('defaultRobotCode');
    let robotCode = code ?? robotList?.[0]?.robotCode;
    if (header?.isTestChat && storageRobot && !!robotList?.find(({ robotCode }) => robotCode === storageRobot)) {
      // 是测试机器人，并且浏览器缓存的机器人 code 在机器人列表中，则使用浏览器缓存的机器人 code
      robotCode = storageRobot;
    }
    
    // 获取脚本URL
    const scriptUrl = ENV === 'dev' 
      ? location.href 
      : Array.from(document.scripts)?.map((script) => script.src).find((url) => (url.indexOf('/super-robot.js') >= 0));
    
    if (!scriptUrl) {
      throw new Error('无法获取脚本URL');
    }

    const url = new URL(scriptUrl);
    const params = new URLSearchParams(url.search);
    if (!robotCode) {
      robotCode = code || params.get('robotCode') || '';
    }
    tokenKey = tokenKey ?? params.get('tokenKey') ?? undefined;
    token = token ?? params.get('token') ?? undefined;

    const tokenData = await getStoToken(tokenKey, token);
    token = tokenData.token || token;
    
    Global.fetchHeader = {
      ...Global.fetchHeader,
      ...header,
      stoToken: token,
      Webusertoken: token,
    }
    
    Global.robotInfo = {
      ...Global.robotInfo,
      robotCode,
    }
    
    // 获取当前脚本的 URL
    if (ENV !== 'dev' && autoloadingCss) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = scriptUrl?.replace('/super-robot.js', '/static/css/main.css');  // 这里填写打包后的 CSS 文件的路径
      document.head.appendChild(link);
    }
    
    if (!robotCode) {
      throw new Error('未提供机器人代码');
    }

    const robotData = await R({
      name: 'robotChat.getDetailByCode',
      data: {
        code: robotCode,
      },
    }) as RobotInfo;

    if (robotData.robotCode) {
      const dom = document.querySelector('#sto-super-robot') as HTMLElement || document.createElement('div');
      dom.id = "sto-super-robot";
      dom.className = styles['sto-super-robot'];
      document.body.appendChild(dom);

      // Create a ref for controlling the AIChart component
      const AIChartRef = React.createRef<AIChartInstance>();

      // Store control methods in robotControlInstance
      robotControlInstance = {
        showRobot: () => {
          if (AIChartRef.current) {
            AIChartRef.current.setBotVisible(true);
          }
        },
        hideRobot: () => {
          if (AIChartRef.current) {
            AIChartRef.current.setBotVisible(false);
          }
        },
        toggleRobot: () => {
          if (AIChartRef.current) {
            // Get current visibility directly from the component
            const isCurrentlyVisible = AIChartRef.current.getBotVisible();
            // Toggle visibility
            AIChartRef.current.setBotVisible(!isCurrentlyVisible);
            return !isCurrentlyVisible;
          }
          return false;
        }
      };

      // Pass the ref to AIChart
      ReactDOM.render(
        React.createElement(AIChart, {
          ref: AIChartRef,
          isTestChat: header?.isTestChat,
          robotList: robotList,
          robotInfo: { ...robotData },
          hideTriggerButton: hideTriggerButton,
          TriggerComponent: TriggerComponent,
          streamUrl
        }),
        dom
      );
    } else {
      let msg = `code: ${robotCode}`
      console.error(`未能获取到机器人信息，${msg}`);
    }

    // Return control methods
    return {
      show: () => robotControlInstance?.showRobot(),
      hide: () => robotControlInstance?.hideRobot(),
      toggle: () => robotControlInstance?.toggleRobot() || false
    };
  } catch (error) {
    console.error('Error initializing widget:', error);
    return {
      show: () => console.error('Robot control unavailable due to initialization error'),
      hide: () => console.error('Robot control unavailable due to initialization error'),
      toggle: () => {
        console.error('Robot control unavailable due to initialization error');
        return false;
      }
    };
  }
};

const getStoToken = (tokenKey?: string, token?: string): Promise<TokenResponse> => {
  // TODO: 临时解决异步获取 stoToken的问题，鉴权模式需要重新规划
  return new Promise((resolve) => {
    if (token) {
      resolve({ token });
      return;
    }
    if (!tokenKey && !token) {
      resolve({});
      return;
    }
    const time = setInterval(() => {
      const stoToken = getCookie(tokenKey) as string || getCookie('stoToken') as string || getCookie('TOKEN') as string || getCookie('token') as string;
      if (stoToken) {
        Global.fetchHeader = {
          ...Global.fetchHeader,
          stoToken: stoToken
        }
        resolve({ token: stoToken });
        clearInterval(time);
      } else {
        console.error('未能获取到stoToken，无法获取机器人信息： tokenKey:', tokenKey);
      }
    }, 500);
  })
}

// Global control methods
export const showRobot = (): void => robotControlInstance?.showRobot();
export const hideRobot = (): void => robotControlInstance?.hideRobot();
export const toggleRobot = (): boolean => robotControlInstance?.toggleRobot() || false;

const init = (): void => {
  let scriptUrl: string | undefined;
  if (ENV === 'dev') {
    scriptUrl = Array.from(document.scripts)?.map((script) => script.src).find((url) => url.indexOf(':3000/superRobot.js?') >= 0) || location.href;
  } else {
    scriptUrl = Array.from(document.scripts)?.map((script) => script.src).find((url) => url.indexOf('/super-robot.js') >= 0);
  }
  
  if (!scriptUrl) {
    console.error('无法获取脚本URL');
    return;
  }

  // 创建一个 URL 对象并获取查询参数
  const url = new URL(scriptUrl);
  const params = new URLSearchParams(url.search);
  const usePattern = params?.get('usePattern');
  const robotCode = params?.get('robotCode');
  const tokenKey = params.get('tokenKey');
  
  if (usePattern !== 'umd') {
    // 自动初始化
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      initWidget({ tokenKey: tokenKey || undefined, robotCode: robotCode || undefined });
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        initWidget({ tokenKey: tokenKey || undefined, robotCode: robotCode || undefined });
      });
    }
  }
}

init();

export default initWidget; 